import { Import, Settings, User, Users } from "lucide-react";
import { CanAccess, useTranslate, useUserMenu } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";

import { ImportPage } from "../misc/ImportPage";
import { PreferencesDialog, PreferencesMenuItem } from "../preferences";

// The nav itself lives in AppSidebar now — this is just the top bar's
// right-hand cluster (theme/refresh/user menu), rendered inside Layout.tsx
// next to the SidebarTrigger.
const Header = () => {
  // Lifted up here (not local to PreferencesMenuItem) because the actual
  // Dialog has to render as a sibling of <UserMenu>, not inside it — Radix
  // unmounts DropdownMenuContent's whole subtree on close, which would
  // unmount the Dialog (and its open state) right along with it.
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  return (
    <div className="flex items-center">
      <ThemeModeToggle />
      <RefreshButton />
      <UserMenu>
        <ProfileMenu />
        <CanAccess resource="sales" action="list">
          <UsersMenu />
        </CanAccess>
        <CanAccess resource="configuration" action="edit">
          <SettingsMenu />
        </CanAccess>
        <PreferencesMenuItem onOpen={() => setPreferencesOpen(true)} />
        <ImportFromJsonMenuItem />
      </UserMenu>
      <PreferencesDialog
        open={preferencesOpen}
        onOpenChange={setPreferencesOpen}
      />
    </div>
  );
};

const UsersMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<UsersMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/sales" className="flex items-center gap-2">
        <Users />
        {translate("resources.sales.name", { smart_count: 2 })}
      </Link>
    </DropdownMenuItem>
  );
};

const ProfileMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ProfileMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User />
        {translate("crm.profile.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const SettingsMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<SettingsMenu> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings />
        {translate("crm.settings.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const ImportFromJsonMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ImportFromJsonMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ImportPage.path} className="flex items-center gap-2">
        <Import />
        {translate("crm.header.import_data")}
      </Link>
    </DropdownMenuItem>
  );
};

export default Header;
